import { useMemo } from "react"
import { useParams, useSearch } from "@tanstack/react-router"

import { authorableAt, usePlanningUnits } from "~/components/demand-2/shared/usePlanningUnits"
import { type ICollectionDetails, useGetCollectionsWithDetails } from "~/hooks/useCollections"
import { type CollectionKind, kindOf } from "~/utils/collectionKind"
import { type PlanningLevel } from "~/utils/demandPlanningLevel"

// The ROUTE scope (which collections the page shows), not `DemandScope`, a cell's channel ×
// warehouse × market address.
export type DemandRouteScope = {
  /** A collection id, or a group id when isGroup. NaN at portfolio level. */
  scopeId: number
  isGroup: boolean
  isPortfolio: boolean
  collectionIds: number[]
  /**
   * True while a group / portfolio route waits on the collections list; never on a single
   * collection. Gate page loading on it, or views mount with empty `collectionIds`.
   */
  isResolving: boolean
  groupName: string | null
  collectionName: string | null
  memberCollections: ICollectionDetails[]
  /**
   * A single collection route's group, if any. A group plan covers every member, so a member route
   * points at the group instead of authoring one.
   */
  parentGroup: { id: number; name: string } | null
  /**
   * False until the collections list loads. A single collection route is never `isResolving`, so
   * without this an authoring control would render enabled, then disable.
   */
  parentGroupKnown: boolean
  planningLevel: PlanningLevel | null
  /**
   * Whether this route is the level the plan is authored at (publish). True while the unit
   * is unknown: withholding publish risks a plan nobody can publish.
   */
  authorable: boolean
  /** Null at portfolio level and until the list lands. */
  kind: CollectionKind | null
}

/**
 * The collections the current route operates on: `[collectionId]`, every group member
 * (`?isGroup=true`, where `$collectionId` is the GROUP id), or the whole portfolio.
 * `collectionIds` is empty until the list loads, so guard queries with `collectionIds.length > 0`.
 */
export function useDemand2Scope(): DemandRouteScope {
  const params = useParams({ strict: false }) as { collectionId?: string }
  const search = useSearch({ strict: false }) as { isGroup?: boolean }
  const isPortfolio = params.collectionId == null
  const scopeId = Number(params.collectionId)
  const isGroup = search.isGroup === true

  const { data: collections, isLoading: collectionsLoading } = useGetCollectionsWithDetails()
  const units = usePlanningUnits()
  const isResolving = (isGroup || isPortfolio) && collectionsLoading

  return useMemo(() => {
    if (isPortfolio) {
      return {
        scopeId,
        isGroup: false,
        isPortfolio: true,
        isResolving,
        collectionIds: (collections ?? []).map((c) => c.id),
        groupName: null,
        collectionName: null,
        memberCollections: [],
        parentGroup: null,
        parentGroupKnown: !collectionsLoading,
        // The portfolio spans every plan and authors none itself.
        planningLevel: null,
        authorable: true,
        kind: null,
      }
    }
    if (!isGroup) {
      const own = (collections ?? []).find((c) => c.id === scopeId)
      const groupId = own?.collectionGroupId ?? null
      const unit = units.get(scopeId) ?? null
      return {
        scopeId,
        isGroup: false,
        isPortfolio: false,
        isResolving,
        collectionIds: Number.isFinite(scopeId) ? [scopeId] : [],
        groupName: null,
        collectionName: own?.name ?? null,
        memberCollections: [],
        parentGroup:
          groupId != null ? { id: groupId, name: own?.collectionGroup?.name ?? "its" } : null,
        parentGroupKnown: !collectionsLoading,
        planningLevel: unit?.level ?? null,
        authorable: authorableAt(unit, false),
        kind: own ? kindOf(own) : null,
      }
    }
    const members = (collections ?? []).filter((c) => c.collectionGroupId === scopeId)
    // Any member: at level `group` all map to the group unit; at `collection` each to its own.
    const unit = members[0] ? (units.get(members[0].id) ?? null) : null
    return {
      scopeId,
      isGroup: true,
      isPortfolio: false,
      isResolving,
      collectionIds: members.map((c) => c.id),
      groupName: members[0]?.collectionGroup?.name ?? null,
      collectionName: null,
      memberCollections: members,
      parentGroup: null,
      parentGroupKnown: !collectionsLoading,
      planningLevel: unit?.level ?? null,
      authorable: authorableAt(unit, true),
      // Groups are homogeneous, so any member answers for all of them.
      kind: members[0] ? kindOf(members[0]) : null,
    }
  }, [collections, collectionsLoading, scopeId, isGroup, isPortfolio, isResolving, units])
}
